import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Building2, CheckCircle2, ArrowRight } from 'lucide-react';
import FormInput from '../../components/FormInput';
import PasswordChecklist from '../../components/PasswordChecklist';
import PhoneInput from '../../components/PhoneInput';
import { errorMessage } from '../../api/client';
import * as authApi from '../../api/auth.api';
import { BRAND_NAME } from '../../utils/brand';
import {
  isStrongClinicPassword,
  CLINIC_PASSWORD_HELPER,
  CLINIC_PASSWORD_WEAK_MSG,
  PASSWORD_MISMATCH_MSG,
} from '../../utils/passwordPolicy';

// Client 2026-08-28 changes:
//   • Phone: split into country picker + smart auto-formatting input, with
//     leading-zero stripping. Helper caption + validation copy tailored
//     for Malaysian mobile numbers.
//   • Password: min 8 chars (was 12), grouped-rule checklist. New weak-
//     password message.
//   • Removed T&C / Privacy checkboxes — acceptance is captured earlier
//     in the mobile onboarding flow.
//   • Success screen "Back to sign in" now goes to /portal-login (not the
//     patient /login).
function ClinicSignup() {
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    clinicName: '',
  });
  const [phone, setPhone] = useState({ rawDigits: '', e164: '', dialCode: '+60' });
  const [phoneErr, setPhoneErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [submitted, setSubmitted] = useState(false);

  function set(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  function validatePhone() {
    if (phone.dialCode === '+60') {
      const n = phone.rawDigits.length;
      if (n < 9 || n > 10) {
        return 'Please enter a valid 9 to 10 digit Malaysian phone number.';
      }
    } else if (phone.rawDigits.length < 6) {
      return 'Please enter a valid phone number.';
    }
    return '';
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setPhoneErr('');

    const pErr = validatePhone();
    if (pErr) { setPhoneErr(pErr); return; }

    if (!isStrongClinicPassword(form.password)) {
      setErr(CLINIC_PASSWORD_WEAK_MSG);
      return;
    }
    if (form.password !== form.confirmPassword) {
      setErr(PASSWORD_MISMATCH_MSG);
      return;
    }

    setBusy(true);
    try {
      const payload = {
        name:       form.name,
        email:      form.email,
        password:   form.password,
        clinicName: form.clinicName,
        phone:      phone.e164,
      };
      await authApi.clinicRegister(payload);
      setSubmitted(true);
      toast.success('Clinic registered');
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <div className="auth-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
          <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
        </div>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={22} color="var(--success, #16a34a)" />
          Application submitted
        </h2>
        <div className="auth-sub">
          Your clinic is pending admin approval. Once approved, you can sign in with your email
          and password at the clinic portal.
        </div>

        <a
          href="/portal-login?welcome=1"
          className="btn btn-primary btn-block"
          style={{ marginTop: 'var(--s-4)' }}
        >
          Continue to sign in <ArrowRight size={16} />
        </a>
      </div>
    );
  }

  return (
    <div className="auth-card">
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
        <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
      </div>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Building2 size={22} color="var(--brand-700)" />
        Clinic sign-up
      </h2>
      <div className="auth-sub">
        Apply to onboard your clinic. An admin will review your application before bookings can begin.
      </div>

      <form onSubmit={onSubmit}>
        <FormInput label="Clinic name" value={form.clinicName}
          onChange={(e) => set('clinicName', e.target.value)} placeholder="KL Wellness Clinic" required />
        <FormInput label="Contact person — full name" value={form.name}
          onChange={(e) => set('name', e.target.value)} placeholder="Dr. Aisha binti Rahman" required />
        <FormInput label="Email" type="email" autoComplete="email" value={form.email}
          onChange={(e) => set('email', e.target.value)} placeholder="contact@clinic.com" required />

        <PhoneInput
          label="Phone"
          defaultDialCode="+60"
          onChange={(v) => { setPhone(v); if (phoneErr) setPhoneErr(''); }}
          helper="Mobile Number (for SMS verification)"
          error={phoneErr}
          required
        />

        <FormInput
          label="Password"
          type="password"
          autoComplete="new-password"
          value={form.password}
          onChange={(e) => set('password', e.target.value)}
          placeholder="Enter your password"
          hint={CLINIC_PASSWORD_HELPER}
          required
        />
        <PasswordChecklist password={form.password} variant="clinic" />

        <FormInput
          label="Confirm Password"
          type="password"
          autoComplete="new-password"
          value={form.confirmPassword}
          onChange={(e) => set('confirmPassword', e.target.value)}
          placeholder="Re-enter your password"
          required
          error={
            form.confirmPassword && form.confirmPassword !== form.password
              ? PASSWORD_MISMATCH_MSG
              : ''
          }
        />

        {err && <div className="notice warning" style={{ marginTop: 'var(--s-4)' }}>{err}</div>}

        <button type="submit" className="btn btn-primary btn-block"
          disabled={busy} style={{ marginTop: 'var(--s-3)' }}>
          {busy ? <span className="spinner" /> : <Building2 size={16} />}
          {busy ? 'Submitting…' : 'Submit clinic application'}
        </button>
      </form>

      <div className="divider" />
      <div className="text-sm muted" style={{ textAlign: 'center' }}>
        Already onboarded? <Link to="/portal-login">Sign in</Link>
      </div>
    </div>
  );
}

export default ClinicSignup;
